import clsx from "clsx";
import { LoadingIndicator, NumberBadge, SideNavGroup } from "@k8slens/lds";
import { type Business, ForbiddenException, type UserWithEmail } from "lens-platform-sdk";

import { isDevelopment, isStagingOrTest } from "src/constants";
import { useBillingPageUrl, useBusinessBillingPageUrl } from "src/hooks/useBillingPageUrl";
import { useGetLicense } from "src/hooks/useGetLicense";
import { features } from "src/utils/license";
import { useBusinessUsers } from "src/hooks/useBusinessUsers";

import MainNavigation from "src/components/MainNavigation/MainNavigation";
import MainNavLink from "src/components/MainNavigation/MainNavigationItem";

import styles from "./Layout.module.css";

interface Props {
  className?: string;
  children: React.ReactNode;
  logout: () => void;
  profile: UserWithEmail | null;
  businessId?: string;
  businessRequestCount?: number;
  business?: Business;
}

export default function Layout({
  className,
  children,
  logout,
  profile,
  business,
  businessId,
  businessRequestCount,
}: Props) {
  const isExternal = business?.external === true;

  // No need to fetch urls for user if we have businessId
  const billingPageUser = businessId ? undefined : profile?.username;

  const { accountUrl: userAccountUrl } = useBillingPageUrl(billingPageUser);
  const { accountUrl: businessAccountUrl } = useBusinessBillingPageUrl(businessId);
  const { error: errorGetBusinessUser } = useBusinessUsers(businessId);

  // "Member" has not permission to get business users
  const isBusinessMember = errorGetBusinessUser instanceof ForbiddenException;
  const accountUrl = businessAccountUrl || userAccountUrl;
  const license = useGetLicense();

  if (!profile) {
    return (
      <div className={clsx(styles.layout, className)}>
        <LoadingIndicator size="xxl" />
      </div>
    );
  }

  const isSupportEnabled = license?.features?.includes?.(features.support);
  const showEnterpriseSettings = businessId && !isBusinessMember;

  return (
    <div className={clsx(styles.layout, className)}>
      <MainNavigation
        profile={profile}
        logout={logout}
        className={styles.sidebar}
        business={business}
        businessId={businessId}
      >
        <SideNavGroup aria-label="Navigation" className={styles.homeLink}>
          <MainNavLink to={businessId ? `/business/${businessId}` : "/home"} title="Home" exact />
        </SideNavGroup>
        <SideNavGroup label="Settings">
          <MainNavLink to={businessId ? `/business/${businessId}/profile` : "/profile"} title="Profile" />
          {businessId && !isBusinessMember && <MainNavLink to={`/business/${businessId}/account`} title="Account" />}
          {!businessId && <MainNavLink to="/account" title="Account" />}
          {!businessId && <MainNavLink to="/connected-accounts" title="Connected Accounts" />}
          {businessId ? (
            <MainNavLink to={`/business/${businessId}/billing-details`} title="Billing Details" disabled={isExternal} />
          ) : (
            <MainNavLink to="/billing-details" title="Billing Details" disabled={!accountUrl || isExternal} />
          )}
          {businessId ? null : <MainNavLink to="/emails" title="Emails" />}
          {businessId ? <MainNavLink to={`/business/${businessId}/subscriptions`} title="Manage Seats" /> : null}
          {(isStagingOrTest || isDevelopment) && businessId ? (
            <MainNavLink
              to={`/business/${businessId}/users`}
              title={
                <span className={styles.businessUsers}>
                  <span>Users</span>
                  <NumberBadge
                    className={styles.badge}
                    type="error"
                    value={businessRequestCount}
                    title={`${businessRequestCount} pending request(s).`}
                  />
                </span>
              }
            />
          ) : null}
          {businessId ? (
            <MainNavLink to="/support" title="Support" />
          ) : (
            <MainNavLink to="/support" title="Support" disabled={!isSupportEnabled} />
          )}
        </SideNavGroup>
        {showEnterpriseSettings && (
          <SideNavGroup label="Control Center">
            <MainNavLink to={`/business/${businessId}/parent-child-settings`} title="Account Hierarchy" />
            <MainNavLink to={`/business/${businessId}/feature-flag-settings`} title="Feature Management" />
          </SideNavGroup>
        )}
        {businessId ? (
          <SideNavGroup label="Other Services">
            <MainNavLink to={`/business/${businessId}/app-iq`} title="Lens AppIQ" />
          </SideNavGroup>
        ) : null}
      </MainNavigation>
      <main className={styles.main}>{children}</main>
    </div>
  );
}
